import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { useGetNotification } from "../services/api/notification.api";
import { useErrorResponse } from "../hooks/useErrorResponse";
import { useAuth } from "../hooks";
import CustomTab from "../components/common/CustomTab";
import CustomTabPanel from "../components/Dashboard/Talent/BookingList/CustomTabPanel";
import { useAcceptGroupInvitation } from "../services/api/booking.api";
import { useCardName } from "../services/api/card.api";


interface NotificationData {
  booking_date_time: string;
  booking_id: string;
  card_id: string;
  channel_name: string;
  id: string;
  invited_by: string;
  payment_status: string;
  price: string;
  status: string;
  type: string;
  username: string;
}

export interface NotificationFullData {
  data: NotificationData;
  id: number;
  is_read: boolean;
  message: string;
  notification_type: string;
  read_at: any;
  title: string;
}

const formatText = (label: string, value: string | number) => {
  let color = "var(--neutralBlack)";

  if (value === "Unpaid") color = "var(--destructive-500)";
  else if (value === "Accepted") color = "var(--success-600)";
  else if (value === "Rejected") color = "var(--destructive-600)";
  else if (value === "Paid") color = "var(--secondary-600)";

  return (
    <Typography sx={{ font: "var(--body-xs)", color: "var(--neutralBlack)" }}>
      {label}:{" "}
      <Box component="span" sx={{ font: "var(--semibold-xs)", color }}>
        {value}
      </Box>
    </Typography>
  );
};

const UserNotification = () => {
  const {
    data: notificationData,
    error: notificationError,
    isLoading: isNotificationLoading,
    refetch: refetchNotification,
  } = useGetNotification({
    key: ["notififation"],
  }) as {
    data: { data: NotificationFullData[] };
    error: any;
    isLoading: boolean;
    refetch: any;
  };

  const { mutate: acceptGroupInvitation } = useAcceptGroupInvitation({
    key: ["group-invitation"],
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { userInfo } = useAuth();
  const purchasedChips = userInfo?.wallet_purchase?.available_chips || 0;
  const earnedChips = userInfo?.wallet_earning?.available_chips || 0;
  useErrorResponse(notificationError);

  const notifications = notificationData?.data || [];

  const ALL_NOTIFICATION_TYPES = [
    "Bookings",
    "group_session",
    "regular_session",
  ];

  const groupedNotifications = notifications.reduce(
    (acc, notif) => {
      const type = notif.data.type;

      const key =
        type === "booking_invite" || type === "booking_accepted"
          ? "Bookings"
          : type;

      acc[key] = acc[key] || [];
      acc[key].push(notif);
      return acc;
    },
    {} as Record<string, NotificationFullData[]>
  );

  const tabKeys = ALL_NOTIFICATION_TYPES;
  const [tabValue, setTabValue] = useState<string | number>(tabKeys[0] || "");
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [activeNotification, setActiveNotification] =
    useState<NotificationFullData | null>(null);

  useEffect(() => {
    if (tabKeys.length && !tabValue) setTabValue(tabKeys[0]);
  }, [tabKeys, tabValue]);

  const handleOpen = (notif: NotificationFullData) => {
    setActiveNotification(notif);
    setOpenPaymentModal(true);
  };

  const handleInvitationStatus = (
    booking_id: number,
    notification_id: number,
    status: string,
    selfPay: boolean
  ) => {
    try {
      acceptGroupInvitation(
        {
          id: booking_id,
          notification_id: notification_id,
          status,
        },
        {
          onSuccess: () => {
            refetchNotification();
            setOpenPaymentModal(false);
            if (status === "Accepted") {
              enqueueSnackbar("You have accepted the invitation", {
                variant: "success",
              });
            }
            if (status === "Rejected") {
              enqueueSnackbar("You have rejected the invitation", {
                variant: "warning",
              });
            }
            if (
              selfPay &&
              status === "Accepted" &&
              activeNotification?.data?.price
            ) {
              setUser({
                ...userInfo!,
                wallet_purchase: {
                  ...userInfo!.wallet_purchase,
                  available_chips:
                    purchasedChips > 0
                      ? purchasedChips - Number(activeNotification.data.price)
                      : purchasedChips,
                },
                wallet_earning: {
                  ...userInfo!.wallet_earning,
                  available_chips:
                    purchasedChips === 0
                      ? earnedChips - Number(activeNotification.data.price)
                      : earnedChips,
                },
              });
            }
          },

          onError: (error: any) => {
            const backendMessage =
              error?.response?.data?.message ||
              error?.message ||
              "You do not have enough chips.";

            enqueueSnackbar(backendMessage, { variant: "error" });
          },
        }
      );
    } catch (error: any) {}
  };

  const typeLabelMap: Record<string, string> = {
    booking: "Bookings",
    group_session: "Group Session",
    regular_session: "Regular Session",
  };

  const { getCardById } = useCardName();

  const cardName = (card_id: string | number) => {
    const cardData = getCardById(card_id);
    return cardData ? cardData?.title : "";
  };

  if (isNotificationLoading) return <div>Loading...</div>;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={3}
      width="100%"
      py={1}
      alignItems="center"
    >
      <CustomTab
        tabs={tabKeys.map((type) => ({
          label: typeLabelMap[type] || type,
          value: type,
        }))}
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        centerAligned
      />
      {tabKeys.map((type) => (
        <CustomTabPanel key={type} value={tabValue} index={type}>
          {(groupedNotifications[type] || []).length > 0 ? (
            groupedNotifications[type].map((notif, idx) => {
              const {
                title,
                message,
                data: {
                  username,
                  invited_by,
                  booking_date_time,
                  payment_status,
                  price,
                  type: notifType,
                  channel_name,
                  card_id,
                },
              } = notif;
              const isUnpaid = payment_status === "Unpaid";
              return (
                <Box
                  key={notif.id}
                  sx={{
                    width: isMobile ? "100%" : 480,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "20px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      <Typography sx={{ font: "var(--semibold-md)" }}>
                        {title}
                      </Typography>
                      <Typography
                        sx={{ font: "var(--body-xs)", lineHeight: "12px" }}
                      >
                        {message}
                      </Typography>
                      {formatText("Talent", username)}
                      {card_id && formatText("Card Name", cardName(card_id))}
                      {invited_by && formatText("Invited by", invited_by)}
                      {booking_date_time &&
                        formatText(
                          "Date",
                          dayjs(booking_date_time).format("YYYY-MM-DD")
                        )}
                      {booking_date_time &&
                        formatText(
                          "Time",
                          dayjs(booking_date_time).format("HH:mm")
                        )}
                      {payment_status &&
                        formatText("Payment Status", payment_status)}
                      {isUnpaid && (
                        <>
                          {formatText("Required Chips", price)}
                          {formatText(
                            "Your current Chips",
                            purchasedChips + earnedChips
                          )}
                        </>
                      )}
                    </Box>

                    {notifType === "booking_invite" && (
                      <Box
                        sx={{
                          minWidth: 110,
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        {/* {!notif.data?.status && ( */}
                        {notif?.data?.status === "Accepted" && (
                          <Typography
                            sx={{
                              textAlign: "right",
                              color: "var(--secondary-600)",
                              font: "var(--semibold-sm)",
                            }}
                          >
                            Accepted
                          </Typography>
                        )}

                        {notif?.data?.status === "Rejected" && (
                          <Typography
                            sx={{
                              textAlign: "right",
                              color: "var(--destructive-600)",
                              font: "var(--semibold-sm)",
                            }}
                          >
                            Rejected
                          </Typography>
                        )}

                        {notif?.data?.status === "Pending" && (
                          <>
                            <Button
                              onClick={() =>
                                isUnpaid
                                  ? handleOpen(notif)
                                  : handleInvitationStatus(
                                      Number(notif.data.booking_id),
                                      Number(notif.id),
                                      "Accepted",
                                      invited_by && isUnpaid ? true : false
                                    )
                              }
                              variant="contained"
                              sx={{
                                background: "var(--button-gradient)",
                                color: "var(--neutralWhite)",
                                textTransform: "none",
                                font: "var(--semibold-xxs)",
                                padding: "var(--spacing-4x)",
                                borderRadius: "8px !important",
                                boxShadow: "none",
                                "&:hover": {
                                  background: "var(--buton-gradient-hover)",
                                },
                              }}
                            >
                              {invited_by && isUnpaid
                                ? "Accept and Pay"
                                : "Accept"}
                            </Button>
                            <Button
                              onClick={() =>
                                handleInvitationStatus(
                                  Number(notif.data.booking_id),
                                  Number(notif.id),
                                  "Rejected",
                                  false
                                )
                              }
                              variant="contained"
                              sx={{
                                background: "var(--destructive-600)",
                                color: "var(--neutralWhite)",
                                textTransform: "none",
                                font: "var(--semibold-xxs)",
                                padding: "var(--spacing-4x)",
                                borderRadius: "8px !important",
                                boxShadow: "none",
                              }}
                            >
                              Reject
                            </Button>
                          </>
                        )}
                      </Box>
                    )}

                    {(notifType === "regular_session" ||
                      notifType === "group_session") && (
                      <Box
                        sx={{
                          minWidth: 110,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Button
                          onClick={() => {
                            notifType === "regular_session" &&
                              navigate(
                                `/user/session/${channel_name}?bookingId=${notif?.data?.id}`
                              );
                            notifType === "group_session" &&
                              navigate(
                                `/user/group-session/${channel_name}?bookingId=${notif?.data?.id}`
                              );
                          }}
                          variant="contained"
                          sx={{
                            background: "var(--button-gradient)",
                            color: "var(--neutralWhite)",
                            textTransform: "none",
                            font: "var(--semibold-xxs)",
                            padding: "var(--spacing-4x)",
                            borderRadius: "8px !important",
                            boxShadow: "none",

                            "&:hover": {
                              background: "var(--buton-gradient-hover)",
                            },
                          }}
                        >
                          Join Session
                        </Button>
                      </Box>
                    )}

                    {notifType === "booking_accepted" && (
                      <Box
                        sx={{
                          minWidth: 110,
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                        }}
                      >
                        <Button
                          onClick={() => navigate("/sessions/")}
                          variant="contained"
                          sx={{
                            background: "var(--button-gradient)",
                            color: "var(--neutralWhite)",
                            textTransform: "none",
                            font: "var(--semibold-xxs)",
                            padding: "var(--spacing-4x)",
                            borderRadius: "8px !important",
                            boxShadow: "none",
                            "&:hover": {
                              background: "var(--buton-gradient-hover)",
                            },
                          }}
                        >
                          View
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {idx < groupedNotifications[type].length - 1 && (
                    <Divider sx={{ my: 2, width: isMobile ? "100%" : 480 }} />
                  )}
                </Box>
              );
            })
          ) : (
            <Typography>No notifications in this category.</Typography>
          )}
        </CustomTabPanel>
      ))}

      {activeNotification?.data?.payment_status === "Unpaid" && (
        <Dialog
          open={openPaymentModal}
          onClose={() => setOpenPaymentModal(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirm Payment</DialogTitle>
          <DialogContent dividers>
            <Typography>
              You are about to pay{" "}
              <strong>{activeNotification?.data?.price} chips</strong>. Do you
              want to continue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpenPaymentModal(false)}
              sx={{
                border: "1px solid var(--neutral-300)",
                textTransform: "none",
                font: "var(--semibold-xxs)",
                padding: "var(--spacing-4x)",
                borderRadius: "8px !important",
                color: "var(--neutral-500)",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                handleInvitationStatus(
                  Number(activeNotification?.data?.booking_id),
                  Number(activeNotification?.id),
                  "Accepted",
                  true
                )
              }
              variant="contained"
              sx={{
                background: "var(--button-gradient)",
                color: "var(--neutralWhite)",
                textTransform: "none",
                font: "var(--semibold-xxs)",
                padding: "var(--spacing-4x)",
                borderRadius: "8px !important",
                boxShadow: "none",
                "&:hover": { background: "var(--buton-gradient-hover)" },
              }}
            >
              Confirm and Pay
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UserNotification;
